import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Store, Wallet, Plus, QrCode, History, LogOut, Menu, X,
  CreditCard, Euro, Percent, CheckCircle2, Clock, XCircle,
  Camera, User, Scan, AlertCircle, Pencil, PauseCircle, PlayCircle, Filter, Search, Ticket, ChevronDown
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import Logo from '../components/Logo';

const OfferPreview = ({ offer, userPointValue = 0.07, merchantPointValue = 0.10 }) => {
  const basePrice = Number(offer?.base_price) || 100;
  const pointsAwarded = Number(offer?.points_awarded ?? offer?.discount_percentage) || 0;
  const clientValue = pointsAwarded * userPointValue;
  const merchantCost = pointsAwarded * merchantPointValue;
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm max-w-sm">
      <div className="relative w-full h-40 bg-slate-100 flex items-center justify-center">
        {offer?.image_url ? (
          <img src={offer.image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-slate-300 text-xs">Nessuna immagine</div>
        )}
        {pointsAwarded > 0 && (
          <div className="absolute top-3 left-3 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold">
            {Math.round(pointsAwarded)} punti / €{Math.round(basePrice)}
          </div>
        )}
        {offer?.category && (
          <div className="absolute top-3 right-3 bg-white/90 text-slate-700 px-2 py-0.5 rounded-full text-xs font-medium">
            {offer.category}
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="font-semibold text-slate-900 line-clamp-1">{offer?.title || 'Titolo proposta'}</h4>
        <p className="text-xs text-slate-500 mt-0.5">{offer?.company_name}{offer?.company_city ? ` · ${offer.company_city}` : ''}</p>
        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{offer?.description || 'Descrizione proposta…'}</p>
        {offer?.conditions && <p className="text-xs text-slate-400 mt-2 italic line-clamp-1">{offer.conditions}</p>}
        {pointsAwarded > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 rounded bg-emerald-50">
              <p className="text-emerald-700 font-semibold">Cliente riceve</p>
              <p className="text-emerald-900"><strong>{Math.round(pointsAwarded)} punti</strong></p>
            </div>
            <div className="p-2 rounded bg-slate-50">
              <p className="text-slate-500 font-semibold">Ti costa</p>
              <p className="text-slate-800">€{merchantCost.toFixed(2)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [pointsCfg, setPointsCfg] = useState({ user_point_value: 0.07, merchant_point_value: 0.10 });
  const [offers, setOffers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedUser, setScannedUser] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [transactionData, setTransactionData] = useState({ amount_spent: '', offer_id: '' });
  const [newOfferOpen, setNewOfferOpen] = useState(false);
  const [editOfferOpen, setEditOfferOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [newOffer, setNewOffer] = useState({
    title: '', description: '', category: '', base_price: '', points_awarded: '',
    start_date: '', end_date: '', max_uses: '', conditions: '', image_url: ''
  });
  const [txFilterDays, setTxFilterDays] = useState('all');
  const [txFilterUser, setTxFilterUser] = useState('');
  const [txDetail, setTxDetail] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [creatingTransaction, setCreatingTransaction] = useState(false);
  const scannerRef = useRef(null);
  const transactionRequest = useRef(null);
  const paymentRequest = useRef({});



  const fetchData = useCallback(async () => {
    try {
      const [companyRes, offersRes, transactionsRes, categoriesRes, pointsRes] = await Promise.all([
        api.get(`/companies/${user.company_id}`),
        api.get('/offers/company'),
        api.get('/transactions'),
        api.get('/categories'),
        api.get('/company/settings/points').catch(() => ({ data: { user_point_value: 0.07, merchant_point_value: 0.10 } })),
      ]);
      setCompany(companyRes.data);
      setOffers(offersRes.data);
      setTransactions(transactionsRes.data);
      setCategories(categoriesRes.data.categories);
      setPointsCfg(pointsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [user.company_id]);

  const checkPaymentStatus = useCallback(async () => {
    const sessionId = searchParams.get('session_id');
    const type = searchParams.get('type');
    
    if (sessionId) {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success(type === 'registration' ? 'Iscrizione completata!' : 'Ricarica completata!');
          fetchData();
        }
      } catch (error) {
        console.error('Payment status error:', error);
      }
      // Clear URL params
      window.history.replaceState({}, '', '/dashboard/company');
    }
  }, [searchParams, fetchData]);

  useEffect(() => { fetchData(); checkPaymentStatus(); }, [fetchData, checkPaymentStatus]);

  const handleRegistrationPayment = async () => {
    try {
      const response = await api.post('/payments/registration', {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
    }
  };

  const handleTopUp = async (amount) => {
    try {
      const response = await api.post('/payments/topup', {
        amount: amount,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella ricarica');
    }
  };

  const handleScanQR = async (qrData = null) => {
    const code = (qrData || scanInput || '').trim();
    if (!code.startsWith('AIAC-USER:') && !code.startsWith('AIAC-COUPON:')) {
      toast.error('QR Code non valido');
      return;
    }

    try {
      const response = await api.get(`/scan/${code}`);
      setScannedUser(response.data);
      setScannerActive(false);
      // If it's a coupon-specific QR, pre-fill the offer_id and skip selection
      if (response.data.scan_type === 'coupon' && response.data.offer) {
        setTransactionData({
          user_id: response.data.user.id,
          offer_id: response.data.offer.id,
          amount_spent: '',
        });
        toast.success(`${response.data.user.name} · ${response.data.offer.title}`);
      } else {
        toast.success(`Utente trovato: ${response.data.user.name}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Utente non trovato');
    }
  };

  const startCameraScanner = async () => {
    setScannerActive(true);
    // Wait for DOM node to render, then start Html5Qrcode directly (no dropdowns, straight to back camera)
    setTimeout(async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const el = document.getElementById('qr-reader');
        if (!el) return;
        // Reset the container to avoid double-init
        el.innerHTML = '';
        const html5QrCode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5QrCode;
        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          async (decodedText) => {
            try { await html5QrCode.stop(); } catch (e) {}
            setScannerActive(false);
            handleScanQR(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error('Camera start error:', err);
        toast.error('Impossibile avviare la fotocamera. Verifica i permessi del browser.');
        setScannerActive(false);
      }
    }, 100);
  };

  const stopCameraScanner = async () => {
    if (scannerRef.current) {
      try {
        if (typeof scannerRef.current.stop === 'function') {
          await scannerRef.current.stop();
        } else if (typeof scannerRef.current.clear === 'function') {
          await scannerRef.current.clear();
        }
      } catch (e) {}
      scannerRef.current = null;
    }
    setScannerActive(false);
  };

  const handleCreateTransaction = async () => {
    if (creatingTransaction) return;
    if (!scannedUser || !Number.isFinite(Number(transactionData.amount_spent)) || Number(transactionData.amount_spent) <= 0 || !transactionData.offer_id) {
      toast.error('Compila tutti i campi');
      return;
    }

    const fingerprint = JSON.stringify([scannedUser.user.id, transactionData.offer_id, Number(transactionData.amount_spent)]);
    if (transactionRequest.current?.fingerprint !== fingerprint) {
      transactionRequest.current = { fingerprint, id: crypto.randomUUID() };
    }
    setCreatingTransaction(true);
    try {
      await api.post('/transactions', {
        request_id: transactionRequest.current.id,
        user_id: scannedUser.user.id,
        offer_id: transactionData.offer_id,
        amount_spent: parseFloat(transactionData.amount_spent)
      });
      transactionRequest.current = null;
      toast.success('Transazione creata! In attesa di conferma utente.');
      setScannerOpen(false);
      setScannedUser(null);
      setScanInput('');
      setTransactionData({ amount_spent: '', offer_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella transazione');
    } finally {
      setCreatingTransaction(false);
    }
  };

  const handleCreateOffer = async () => {
    try {
      const base_price = parseFloat(newOffer.base_price);
      const points_awarded = parseFloat(newOffer.points_awarded);
      if (!base_price || base_price <= 0) {
        toast.error('Inserisci una spesa di riferimento valida');
        return;
      }
      if (isNaN(points_awarded) || points_awarded < 0) {
        toast.error('Inserisci un numero di punti valido');
        return;
      }
      await api.post('/offers', {
        ...newOffer,
        base_price,
        points_awarded,
        max_uses: newOffer.max_uses ? parseInt(newOffer.max_uses) : null
      });
      toast.success('Proposta creata con successo!');
      setNewOfferOpen(false);
      setNewOffer({
        title: '', description: '', category: '', base_price: '', points_awarded: '',
        start_date: '', end_date: '', max_uses: '', conditions: '', image_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione proposta');
    }
  };

  // File → base64 (compressed). Stored inline in Mongo as image_url data URL.
  const compressImageToBase64 = (file, maxSide = 900, quality = 0.78) => new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (file.size > 5 * 1024 * 1024) {
      reject(new Error('Immagine troppo grande (max 5 MB)'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Impossibile leggere il file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('File immagine non valido'));
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handleImageSelect = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImageToBase64(file);
      if (target === 'new') setNewOffer((p) => ({ ...p, image_url: dataUrl }));
      else setEditingOffer((p) => ({ ...p, image_url: dataUrl }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = '';
    }
  };

  const openEditOffer = (offer) => {
    setEditingOffer({
      id: offer.id,
      title: offer.title || '',
      description: offer.description || '',
      category: offer.category || '',
      base_price: String(offer.base_price ?? 100),
      points_awarded: String(offer.points_awarded ?? offer.discount_percentage ?? ''),
      start_date: offer.start_date ? offer.start_date.slice(0, 10) : '',
      end_date: offer.end_date ? offer.end_date.slice(0, 10) : '',
      max_uses: offer.max_uses != null ? String(offer.max_uses) : '',
      conditions: offer.conditions || '',
      image_url: offer.image_url || ''
    });
    setEditOfferOpen(true);
  };

  const handleUpdateOffer = async () => {
    if (!editingOffer) return;
    try {
      const base_price = parseFloat(editingOffer.base_price);
      const points_awarded = parseFloat(editingOffer.points_awarded);
      if (!base_price || base_price <= 0) {
        toast.error('Inserisci una spesa di riferimento valida');
        return;
      }
      if (isNaN(points_awarded) || points_awarded < 0) {
        toast.error('Inserisci un numero di punti valido');
        return;
      }
      await api.put(`/offers/${editingOffer.id}`, {
        title: editingOffer.title,
        description: editingOffer.description,
        category: editingOffer.category,
        base_price,
        points_awarded,
        start_date: editingOffer.start_date,
        end_date: editingOffer.end_date,
        max_uses: editingOffer.max_uses ? parseInt(editingOffer.max_uses) : null,
        conditions: editingOffer.conditions,
        image_url: editingOffer.image_url
      });
      toast.success('Proposta aggiornata con successo!');
      setEditOfferOpen(false);
      setEditingOffer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento proposta');
    }
  };

  const handleToggleOfferStatus = async (offer) => {
    const newStatus = offer.status === 'active' ? 'suspended' : 'active';
    try {
      await api.put(`/offers/${offer.id}/status?status=${newStatus}`);
      toast.success(newStatus === 'active' ? 'Proposta attivata' : 'Proposta sospesa');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    }
  };

  const filteredTransactions = (() => {
    let list = [...transactions];
    if (txFilterDays !== 'all') {
      const days = parseInt(txFilterDays);
      const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
      list = list.filter((tx) => new Date(tx.created_at).getTime() >= threshold);
    }
    if (txFilterUser.trim()) {
      const q = txFilterUser.trim().toLowerCase();
      list = list.filter((tx) =>
        (tx.user_name || '').toLowerCase().includes(q) ||
        (tx.user_id || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  })();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-3">
              <Logo linkTo="/" size="sm" />
              <div className="border-l border-slate-200 pl-3">
                <h1 className="font-semibold text-slate-900">{company?.name}</h1>
                <p className="text-xs text-slate-500">Dashboard Azienda</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                <Wallet className="w-4 h-4 text-slate-600" />
                <span className="font-medium">€{company?.wallet_balance?.toFixed(2) || '0.00'}</span>
              </div>
              <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 p-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg mb-4">
            <Wallet className="w-4 h-4 text-slate-600" />
            <span className="font-medium">Saldo: €{company?.wallet_balance?.toFixed(2) || '0.00'}</span>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Registration Payment Required */}
        {!company?.registration_paid && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Completa l'iscrizione</h3>
                  <p className="text-amber-700">
                    Per attivare il tuo account e creare proposte, completa il pagamento dell'iscrizione di 250€.
                  </p>
                </div>
                <Button 
                  className="bg-amber-500 hover:bg-amber-600"
                  onClick={handleRegistrationPayment}
                  data-testid="pay-registration-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Paga 250€
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Saldo Borsellino</p>
                  <p className="text-2xl font-bold text-slate-900">€{company?.wallet_balance?.toFixed(2)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Proposte Attive</p>
                  <p className="text-2xl font-bold text-slate-900">{offers.filter(o => o.status === 'active').length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Percent className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Transazioni</p>
                  <p className="text-2xl font-bold text-slate-900">{transactions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <History className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Stato</p>
                  <div className="text-lg font-semibold">
                    {company?.registration_paid ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Attivo</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">In attesa</Badge>
                    )}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Store className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="wallet" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 w-full sm:w-auto justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="wallet" data-testid="wallet-tab">Borsellino</TabsTrigger>
            <TabsTrigger value="scanner" data-testid="scanner-tab">Scanner QR</TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab">Proposte</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="transactions-tab">Transazioni</TabsTrigger>
          </TabsList>

          {/* Wallet Tab */}
          <TabsContent value="wallet">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Ricarica Borsellino
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!company?.registration_paid ? (
                  <p className="text-slate-500">Completa prima il pagamento dell'iscrizione.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[50, 75, 100, 125].map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        className="h-24 flex flex-col items-center justify-center gap-2 hover:border-amber-500 hover:bg-amber-50"
                        onClick={() => handleTopUp(amount)}
                        data-testid={`topup-${amount}-btn`}
                      >
                        <Euro className="w-6 h-6 text-amber-500" />
                        <span className="text-xl font-bold">€{amount}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scanner Tab */}
          <TabsContent value="scanner">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scan className="w-5 h-5 text-emerald-600" />
                  Scansiona QR Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {!company?.registration_paid ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-slate-600">Completa prima il pagamento dell'iscrizione per abilitare lo scanner.</p>
                  </div>
                ) : !scannedUser ? (
                  <>
                    {/* Camera Scanner - Primary */}
                    <div className="max-w-xl mx-auto space-y-4">
                      <Card className="border-2 border-emerald-200 shadow-sm">
                        <CardContent className="p-6">
                          {scannerActive ? (
                            <div className="space-y-4">
                              <div className="text-center">
                                <p className="text-sm text-slate-600 mb-2">📱 Inquadra il QR code del cliente</p>
                              </div>
                              <div id="qr-reader" className="mx-auto rounded-lg overflow-hidden" style={{ maxWidth: '340px' }}></div>
                              <Button variant="outline" onClick={stopCameraScanner} className="w-full" data-testid="stop-scanner-btn">
                                <XCircle className="w-4 h-4 mr-2" />
                                Chiudi Fotocamera
                              </Button>
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                                <Camera className="w-10 h-10 text-emerald-600" />
                              </div>
                              <h3 className="text-xl font-bold mb-2">Scansiona QR Cliente</h3>
                              <p className="text-sm text-slate-500 mb-6">Un tap → apri fotocamera → inquadra il QR del cliente.<br />La proposta verrà pre-selezionata automaticamente.</p>
                              <Button
                                onClick={startCameraScanner}
                                size="lg"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg px-8 py-6 text-base"
                                data-testid="start-scanner-btn"
                              >
                                <Camera className="w-5 h-5 mr-2" />
                                Avvia Fotocamera
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Manual Input - collapsible fallback */}
                      {!scannerActive && (
                        <details className="group">
                          <summary className="cursor-pointer text-sm text-slate-500 flex items-center gap-1 justify-center hover:text-slate-700 select-none">
                            <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                            Inserimento manuale del codice
                          </summary>
                          <div className="mt-3 p-4 bg-slate-50 rounded-lg">
                            <div className="flex gap-2">
                              <Input
                                placeholder="AIAC-USER:xxxxx o AIAC-COUPON:xxxxx"
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                                data-testid="qr-scan-input"
                              />
                              <Button onClick={() => handleScanQR()} variant="outline" data-testid="scan-qr-btn">
                                Cerca
                              </Button>
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  </>
                ) : (
                  /* User Found - Transaction Form */
                  <div className="space-y-6">
                    {/* User Info */}
                    <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-emerald-200 flex items-center justify-center">
                            <User className="w-8 h-8 text-emerald-700" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-emerald-800">{scannedUser.user.name}</h3>
                            <p className="text-emerald-600">{scannedUser.user.email}</p>
                            {scannedUser.condominium && (
                              <Badge variant="outline" className="mt-1 border-emerald-300 text-emerald-700">
                                {scannedUser.condominium.name}
                              </Badge>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => { setScannedUser(null); setScanInput(''); }}>
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Transaction Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">Importo Speso (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Es: 50.00"
                          value={transactionData.amount_spent}
                          onChange={(e) => setTransactionData({ ...transactionData, amount_spent: e.target.value })}
                          className="mt-1"
                          data-testid="transaction-amount-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Proposta Applicata</Label>
                        {scannedUser?.scan_type === 'coupon' && scannedUser?.offer ? (
                          <div className="mt-1 p-2.5 rounded-md border border-emerald-200 bg-emerald-50 flex items-center gap-2" data-testid="locked-offer-display">
                            <Ticket className="w-4 h-4 text-emerald-700" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-emerald-900 truncate">{scannedUser.offer.title}</p>
                              <p className="text-xs text-emerald-700">{scannedUser.offer.discount_percentage} punti/€100 · pre-selezionata dal QR</p>
                            </div>
                          </div>
                        ) : (
                          <Select
                            value={transactionData.offer_id}
                            onValueChange={(value) => setTransactionData({ ...transactionData, offer_id: value })}
                          >
                            <SelectTrigger className="mt-1" data-testid="transaction-offer-select">
                              <SelectValue placeholder="Seleziona una proposta" />
                            </SelectTrigger>
                            <SelectContent>
                              {offers.filter(o => o.status === 'active').map((offer) => (
                                <SelectItem key={offer.id} value={offer.id}>
                                  {offer.title} ({Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} pt / €{Math.round(offer.base_price ?? 100)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Transaction Summary */}
                    {transactionData.amount_spent && transactionData.offer_id && (() => {
                      const offer = offers.find(o => o.id === transactionData.offer_id);
                      const basePrice = offer?.base_price || 100;
                      const pointsAwarded = offer?.points_awarded ?? offer?.discount_percentage ?? 0;
                      const points = parseFloat(transactionData.amount_spent) * pointsAwarded / basePrice;
                      const merchantCost = points * (pointsCfg.merchant_point_value || 0.10);
                      const userValue = points * (pointsCfg.user_point_value || 0.07);
                      return (
                      <Card className="bg-slate-50" data-testid="transaction-summary-card">
                        <CardContent className="p-4">
                          <h4 className="font-medium text-slate-700 mb-3">Riepilogo Transazione</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Importo speso</p>
                              <p className="font-semibold text-lg">€{parseFloat(transactionData.amount_spent).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Proposta</p>
                              <p className="font-semibold text-lg">
                                {Math.round(pointsAwarded)} pt / €{Math.round(basePrice)}
                              </p>
                            </div>
                            <div className="col-span-2 pt-3 border-t">
                              <p className="text-slate-500">Punti per il cliente</p>
                              <p className="font-bold text-2xl text-emerald-600" data-testid="tx-summary-points">
                                {points.toFixed(0)} punti
                              </p>
                            </div>
                            <div className="col-span-2 pt-3 border-t grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-slate-500">Costo per te</p>
                                <p className="font-bold text-lg text-red-600" data-testid="tx-summary-merchant-cost">
                                  €{merchantCost.toFixed(2)}
                                </p>
                                <p className="text-[10px] text-slate-400">detratto dal borsellino</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Valore per il cliente</p>
                                <p className="font-bold text-lg text-emerald-700" data-testid="tx-summary-user-value">
                                  €{userValue.toFixed(2)}
                                </p>
                                <p className="text-[10px] text-slate-400">crediti spese condominiali</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })()}

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => { setScannedUser(null); setScanInput(''); setTransactionData({ amount_spent: '', offer_id: '' }); }}
                        className="flex-1"
                      >
                        Annulla
                      </Button>
                      <Button 
                        onClick={handleCreateTransaction}
                        disabled={!transactionData.amount_spent || !transactionData.offer_id || creatingTransaction}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="create-transaction-btn"
                      >
                        {creatingTransaction ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Crea Transazione
                          </>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-center text-slate-500">
                      Il cliente riceverà una notifica e dovrà confermare la transazione entro 24 ore per ricevere i punti.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Le Tue Proposte</CardTitle>
                {company?.registration_paid && (
                  <Dialog open={newOfferOpen} onOpenChange={setNewOfferOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="new-offer-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nuova Proposta
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Crea Nuova Proposta</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Titolo</Label>
                          <Input
                            value={newOffer.title}
                            onChange={(e) => setNewOffer({ ...newOffer, title: e.target.value })}
                            placeholder="Es: 10 punti ogni €100 su tutti i prodotti"
                            data-testid="offer-title-input"
                          />
                        </div>
                        <div>
                          <Label>Descrizione</Label>
                          <Textarea
                            value={newOffer.description}
                            onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
                            placeholder="Descrizione dettagliata dell'proposta"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Categoria</Label>
                            <Select
                              value={newOffer.category}
                              onValueChange={(value) => setNewOffer({ ...newOffer, category: value })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleziona" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((cat) => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Spesa di riferimento (€) *</Label>
                            <Input
                              type="number"
                              step="1"
                              value={newOffer.base_price}
                              onChange={(e) => setNewOffer({ ...newOffer, base_price: e.target.value })}
                              placeholder="50"
                              data-testid="offer-base-price-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">Spesa cliente su cui vengono emessi i punti (es. €50 per un menu).</p>
                          </div>
                          <div>
                            <Label>Punti emessi *</Label>
                            <Input
                              type="number"
                              step="1"
                              value={newOffer.points_awarded}
                              onChange={(e) => setNewOffer({ ...newOffer, points_awarded: e.target.value })}
                              placeholder="5"
                              data-testid="offer-points-awarded-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">Numero di punti riconosciuti al cliente su quella spesa.</p>
                          </div>
                        </div>

                        {/* Cost Simulator */}
                        {(() => {
                          const price = parseFloat(newOffer.base_price) || 0;
                          const points = parseFloat(newOffer.points_awarded) || 0;
                          if (price <= 0 || points <= 0) return null;
                          const mpv = pointsCfg.merchant_point_value || 0.10;
                          const upv = pointsCfg.user_point_value || 0.07;
                          const wallet = company?.wallet_balance || 0;
                          const yourCost = points * mpv;
                          const clientValue = points * upv;
                          const ticketsCovered = yourCost > 0 ? Math.floor(wallet / yourCost) : 0;
                          return (
                            <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="cost-simulator">
                              <p className="font-semibold text-amber-900 text-sm mb-3 flex items-center gap-2">
                                <Wallet className="w-4 h-4 text-amber-700" />
                                Quanto ti costa questa proposta
                              </p>
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                <div className="bg-white/70 rounded p-3 text-center">
                                  <p className="text-xs text-slate-500">Spesa cliente</p>
                                  <p className="text-2xl font-bold text-slate-900">€{price.toFixed(0)}</p>
                                </div>
                                <div className="bg-white/70 rounded p-3 text-center">
                                  <p className="text-xs text-slate-500">Punti al cliente</p>
                                  <p className="text-2xl font-bold text-emerald-700">{points}</p>
                                </div>
                                <div className="bg-white/70 rounded p-3 text-center">
                                  <p className="text-xs text-slate-500">Ti costa</p>
                                  <p className="text-2xl font-bold text-red-600" data-testid="tx-cost-value">€{yourCost.toFixed(2)}</p>
                                </div>
                              </div>
                              <p className="mt-3 text-xs text-amber-800">
                                Con il borsellino attuale (<b>€{wallet.toFixed(2)}</b>) puoi coprire ≈ <b>{ticketsCovered} vendite</b> di questa proposta. Il cliente riceverà crediti condominiali per <b>€{clientValue.toFixed(2)}</b>.
                              </p>
                              {yourCost > wallet && wallet >= 0 && (
                                <p className="mt-2 text-xs text-red-600 font-medium">⚠ Il borsellino non copre nemmeno una vendita. Ricarica prima di attivare.</p>
                              )}
                            </div>
                          );
                        })()}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Data Inizio</Label>
                            <Input
                              type="date"
                              value={newOffer.start_date}
                              onChange={(e) => setNewOffer({ ...newOffer, start_date: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>Data Fine</Label>
                            <Input
                              type="date"
                              value={newOffer.end_date}
                              onChange={(e) => setNewOffer({ ...newOffer, end_date: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Max Utilizzi (opzionale)</Label>
                          <Input
                            type="number"
                            value={newOffer.max_uses}
                            onChange={(e) => setNewOffer({ ...newOffer, max_uses: e.target.value })}
                            placeholder="Lascia vuoto per illimitato"
                          />
                        </div>
                        <div>
                          <Label>Immagine (opzionale)</Label>
                          <div className="flex items-center gap-3 mt-1">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageSelect(e, 'new')}
                              className="text-sm"
                              data-testid="new-offer-image-input"
                            />
                            {newOffer.image_url && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => setNewOffer({ ...newOffer, image_url: '' })}>
                                <XCircle className="w-4 h-4 mr-1" /> Rimuovi
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Max 5 MB · JPG/PNG/WebP (verrà ridimensionata automaticamente)</p>
                        </div>

                        {/* Live preview */}
                        <div className="pt-2 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Anteprima (come la vedrà l'utente)</p>
                          <OfferPreview
                            userPointValue={pointsCfg.user_point_value}
                            merchantPointValue={pointsCfg.merchant_point_value}
                            offer={{
                              ...newOffer,
                              base_price: parseFloat(newOffer.base_price) || 100,
                              points_awarded: parseFloat(newOffer.points_awarded) || 0,
                              company_name: company?.name,
                              company_city: company?.city,
                            }}
                          />
                        </div>
                        <Button 
                          className="w-full" 
                          onClick={handleCreateOffer}
                          data-testid="submit-offer-btn"
                        >
                          Crea Proposta
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {offers.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna proposta creata</p>
                ) : (
                  <div className="space-y-4">
                    {offers.map((offer) => (
                      <div 
                        key={offer.id} 
                        className="p-4 border border-slate-200 rounded-lg flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {offer.image_url && (
                            <img src={offer.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <h4 className="font-semibold truncate">{offer.title}</h4>
                            <p className="text-sm text-slate-500">{offer.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            offer.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            offer.status === 'suspended' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {offer.status === 'active' ? 'Attiva' : offer.status === 'suspended' ? 'Sospesa' : 'Scaduta'}
                          </Badge>
                          <span className="font-bold text-base text-emerald-700">{Math.round(offer.points_awarded ?? offer.discount_percentage ?? 0)} pt / €{Math.round(offer.base_price ?? 100)}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditOffer(offer)}
                            data-testid={`edit-offer-${offer.id}-btn`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {offer.status !== 'expired' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleOfferStatus(offer)}
                              data-testid={`toggle-offer-${offer.id}-btn`}
                            >
                              {offer.status === 'active' ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Storico Transazioni</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-3 mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-2 flex-1">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <Select value={txFilterDays} onValueChange={setTxFilterDays}>
                      <SelectTrigger className="w-full md:w-48" data-testid="tx-filter-days">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le date</SelectItem>
                        <SelectItem value="1">Oggi</SelectItem>
                        <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                        <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                        <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <Search className="w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca per nome o codice utente..."
                      value={txFilterUser}
                      onChange={(e) => setTxFilterUser(e.target.value)}
                      data-testid="tx-filter-user"
                    />
                    {txFilterUser && (
                      <Button variant="ghost" size="sm" onClick={() => setTxFilterUser('')}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {filteredTransactions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {transactions.length === 0 ? 'Nessuna transazione' : 'Nessuna transazione trovata con i filtri applicati'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500">Mostrate {filteredTransactions.length} di {transactions.length} transazioni</p>
                    {filteredTransactions.map((tx) => (
                      <button 
                        key={tx.id} 
                        type="button"
                        onClick={() => setTxDetail(tx)}
                        className="w-full text-left p-4 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-emerald-300 transition-colors"
                        data-testid={`tx-row-${tx.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold truncate">{tx.user_name}</h4>
                            <p className="text-sm text-slate-500 truncate">{tx.offer_title}</p>
                            <p className="text-xs text-slate-400 font-mono truncate">ID: {tx.user_id?.slice(0, 8)}…</p>
                            <p className="text-xs text-slate-400">
                              {new Date(tx.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold">€{tx.amount_spent.toFixed(2)}</p>
                            <p className="text-sm text-amber-600">Punti: {tx.cashback_gross.toFixed(0)}</p>
                            <Badge className={
                              tx.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                              tx.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {tx.status === 'confirmed' ? <CheckCircle2 className="w-3 h-3 mr-1" /> :
                               tx.status === 'pending' ? <Clock className="w-3 h-3 mr-1" /> :
                               <XCircle className="w-3 h-3 mr-1" />}
                              {tx.status === 'confirmed' ? 'Confermata' : tx.status === 'pending' ? 'In attesa' : 'Rifiutata'}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Offer Dialog */}
      <Dialog open={editOfferOpen} onOpenChange={(open) => { setEditOfferOpen(open); if (!open) setEditingOffer(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Proposta</DialogTitle>
          </DialogHeader>
          {editingOffer && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Titolo</Label>
                <Input
                  value={editingOffer.title}
                  onChange={(e) => setEditingOffer({ ...editingOffer, title: e.target.value })}
                  data-testid="edit-offer-title-input"
                />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea
                  value={editingOffer.description}
                  onChange={(e) => setEditingOffer({ ...editingOffer, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoria</Label>
                  <Select
                    value={editingOffer.category}
                    onValueChange={(value) => setEditingOffer({ ...editingOffer, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Spesa di riferimento (€)</Label>
                  <Input
                    type="number"
                    value={editingOffer.base_price}
                    onChange={(e) => setEditingOffer({ ...editingOffer, base_price: e.target.value })}
                    data-testid="edit-offer-base-price-input"
                  />
                </div>
                <div>
                  <Label>Punti emessi</Label>
                  <Input
                    type="number"
                    value={editingOffer.points_awarded}
                    onChange={(e) => setEditingOffer({ ...editingOffer, points_awarded: e.target.value })}
                    data-testid="edit-offer-points-awarded-input"
                  />
                </div>
              </div>

              {/* Cost Simulator - edit dialog */}
              {(() => {
                const price = parseFloat(editingOffer.base_price) || 0;
                const points = parseFloat(editingOffer.points_awarded) || 0;
                if (price <= 0 || points <= 0) return null;
                const mpv = pointsCfg.merchant_point_value || 0.10;
                const upv = pointsCfg.user_point_value || 0.07;
                const wallet = company?.wallet_balance || 0;
                const yourCost = points * mpv;
                const clientValue = points * upv;
                const ticketsCovered = yourCost > 0 ? Math.floor(wallet / yourCost) : 0;
                return (
                  <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="edit-cost-simulator">
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="w-4 h-4 text-amber-700" />
                      <p className="font-semibold text-amber-900 text-sm">Quanto ti costa questa proposta</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-white/70 rounded p-2 text-center">
                        <p className="text-slate-500">Spesa cliente</p>
                        <p className="font-bold text-slate-900">€{price.toFixed(0)}</p>
                      </div>
                      <div className="bg-white/70 rounded p-2 text-center">
                        <p className="text-slate-500">Punti al cliente</p>
                        <p className="font-bold text-emerald-700">{points}</p>
                      </div>
                      <div className="bg-white/70 rounded p-2 text-center">
                        <p className="text-slate-500">Ti costa</p>
                        <p className="font-bold text-red-600">€{yourCost.toFixed(2)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-amber-800 mt-2">Borsellino €{wallet.toFixed(2)} → copri ≈ <b>{ticketsCovered} vendite</b>. Il cliente riceve crediti per <b>€{clientValue.toFixed(2)}</b>.</p>
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Inizio</Label>
                  <Input
                    type="date"
                    value={editingOffer.start_date}
                    onChange={(e) => setEditingOffer({ ...editingOffer, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={editingOffer.end_date}
                    onChange={(e) => setEditingOffer({ ...editingOffer, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Max Utilizzi (opzionale)</Label>
                <Input
                  type="number"
                  value={editingOffer.max_uses}
                  onChange={(e) => setEditingOffer({ ...editingOffer, max_uses: e.target.value })}
                  placeholder="Lascia vuoto per illimitato"
                />
              </div>
              <div>
                <Label>Condizioni</Label>
                <Textarea
                  value={editingOffer.conditions || ''}
                  onChange={(e) => setEditingOffer({ ...editingOffer, conditions: e.target.value })}
                />
              </div>
              <div>
                <Label>Immagine (opzionale)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, 'edit')}
                    className="text-sm"
                    data-testid="edit-offer-image-input"
                  />
                  {editingOffer.image_url && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingOffer({ ...editingOffer, image_url: '' })}>
                      <XCircle className="w-4 h-4 mr-1" /> Rimuovi
                    </Button>
                  )}
                </div>
              </div>

              {/* Live preview */}
              <div className="pt-2 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Anteprima (come la vedrà l'utente)</p>
                <OfferPreview
                  userPointValue={pointsCfg.user_point_value}
                  merchantPointValue={pointsCfg.merchant_point_value}
                  offer={{
                    ...editingOffer,
                    base_price: parseFloat(editingOffer.base_price) || 100,
                    points_awarded: parseFloat(editingOffer.points_awarded) || 0,
                    company_name: company?.name,
                    company_city: company?.city,
                  }}
                />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setEditOfferOpen(false); setEditingOffer(null); }}>
                  Annulla
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleUpdateOffer}
                  data-testid="save-offer-btn"
                >
                  Salva Modifiche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!txDetail} onOpenChange={(o) => { if (!o) setTxDetail(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Transazione</DialogTitle>
          </DialogHeader>
          {txDetail && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div>
                  <p className="text-xs text-slate-500">Stato</p>
                  <Badge className={
                    txDetail.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                    txDetail.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }>
                    {txDetail.status === 'confirmed' ? 'Confermata' : txDetail.status === 'pending' ? 'In attesa cliente' : 'Rifiutata'}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Importo</p>
                  <p className="font-bold text-lg">€{txDetail.amount_spent.toFixed(2)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Cliente</span>
                  <span className="font-medium">{txDetail.user_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">ID Cliente</span>
                  <span className="font-mono text-xs">{txDetail.user_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Proposta</span>
                  <span className="font-medium text-right">{txDetail.offer_title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Punti accreditati</span>
                  <span className="font-bold text-emerald-600">{txDetail.cashback_gross.toFixed(0)} pt</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Creata il</span>
                  <span>{new Date(txDetail.created_at).toLocaleString('it-IT')}</span>
                </div>
                {txDetail.confirmed_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Confermata il</span>
                    <span>{new Date(txDetail.confirmed_at).toLocaleString('it-IT')}</span>
                  </div>
                )}
                {txDetail.expires_at && txDetail.status === 'pending' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Scade il</span>
                    <span className="text-amber-600">{new Date(txDetail.expires_at).toLocaleString('it-IT')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">ID Transazione</span>
                  <span className="font-mono text-xs">{txDetail.id}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDashboard;
;
